import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import './CommandCenter.css';
import { buildLiquidityAwareTop10, LIQUIDITY_LABEL_FA } from './liquidityShortlist';

import { FINAL_DECISION_LABELS as decisions } from '../../utils/finalDecisionLabels';
const eod = { ENTRY: 'ورود', STRONG_ENTRY: 'ورود قوی', WATCH: 'آماده‌باش', HOLD: 'نگهداری', NO_POSITION: 'بدون موقعیت', WEAKENING: 'تضعیف', EXIT: 'خروج' };
const technical = { BULLISH: 'صعودی', BEARISH: 'نزولی', NEUTRAL: 'خنثی', UNAVAILABLE: 'ناموجود' };
const intraday = { EMERGING: 'فرصت در حال شکل‌گیری', WATCHING: 'زیر نظر', NONE: 'بدون فرصت تازه', UNAVAILABLE: 'ناموجود' };
const number = value => typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('fa-IR', { maximumFractionDigits: 2 }) : '—';
const time = value => value && Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString('fa-IR', { timeZone: 'Asia/Tehran' }) : '—';
export const expiredDecision = (data, now) => data?.items?.some(row => row.valid_until && Date.parse(row.valid_until) <= now);

// Filter Scanner items to only NEW STRONG_CANDIDATE entries (mirrors the
// Scanner's own shortlist eligibility so no new evidence shape is invented).
export function poolNewStrongCandidates(items) {
  if (!Array.isArray(items)) return [];
  return items.filter(item =>
    item && item.scanner_state === 'STRONG_CANDIDATE' && !item.in_money_flow_universe);
}

function DecisionCard({ row }) {
  return <article className="cc-card" data-decision={row.decision}>
    <header><b className="cc-symbol">{row.symbol}</b>
      <strong>{decisions[row.decision]}</strong></header>
    <dl>
      <div><dt>وضعیت EOD</dt><dd>{eod[row.eod_state] || '—'}</dd></div>
      <div><dt>امتیاز EOD</dt><dd>{number(row.action_context?.eod_score)}</dd></div>
      <div><dt>تکنیکال</dt><dd>{technical[row.technical_state] || '—'}</dd></div>
      <div><dt>درون‌روزی</dt><dd>{intraday[row.intraday_state] || '—'}</dd></div>
      <div><dt>مشاهدات متوالی</dt><dd>{number(row.persistence_count)}</dd></div>
      <div><dt>مدت مشاهده · ثانیه</dt><dd>{number(row.observed_duration_seconds)}</dd></div>
    </dl>
    <ul>{(row.reasons || []).map((reason, index) => <li key={index}>{reason}</li>)}</ul>
    {row.decision === 'ENTER' && <p className="cc-notice">وضعیت اجرای سفارش هنوز بررسی نمی‌شود</p>}
    <footer><span>تصمیم: {time(row.decision_at)}</span><span>داده بازار: {time(row.latest_market_data_at)}</span>
      <span>{row.live_current ? 'شواهد زنده معتبر' : 'تأیید زنده معتبر موجود نیست'}</span></footer>
  </article>;
}

export function CommandCenterView({ data, loading, failed, now = Date.now(), onRefresh }) {
  const expired = expiredDecision(data?.decisions, now);
  const usable = data && !loading && !failed && !expired;
  const source = usable ? data.decisions : null;
  const scanner = usable ? data.scanner : null;
  const scannerStale = scanner && (scanner.is_stale || !scanner.fresh_until || Date.parse(scanner.fresh_until) <= now);
  // Liquidity-aware Top-10 drawn from the Scanner's already-filtered
  // NEW STRONG_CANDIDATE pool. Bounded to 10. Does NOT mutate the Scanner
  // payload, ordering, or scores in any way.
  const liquidityTop10 = usable ? buildLiquidityAwareTop10(poolNewStrongCandidates(scanner?.items)) : [];
  const status = loading ? 'در حال دریافت تصمیم‌های موجود…' : failed ? 'دریافت اطلاعات ممکن نشد؛ دوباره تلاش کنید.' : expired ? 'اعتبار داده زنده تمام شد؛ اطلاعات را تازه کنید.' : null;
  return <main className="command-center" dir="rtl" aria-busy={loading}>
    <header className="cc-heading"><div><span>نمای عملیاتی روزانه</span><h1>مرکز تصمیم‌گیری</h1><p>امروز روی کدام نمادها باید تمرکز کنم؟</p></div>
      <button className="cc-refresh" onClick={onRefresh} disabled={loading}>تازه‌سازی</button></header>
    <div className="cc-summary">{Object.entries(decisions).map(([state, label]) => <div key={state} data-decision={state}><span>{label}</span><strong>{number(source?.counts?.[state])}</strong></div>)}</div>
    <p className="cc-meta">کل نمادهای دارای تاریخچه: {number(source?.total)} · آخرین دریافت: {time(data?.receivedAt)} · {source ? (source.market_session_open ? 'جلسه بازار باز است' : 'جلسه بازار بسته است') : 'وضعیت بازار در دسترس نیست'}</p>
    {status && <p className="cc-message" role={failed ? 'alert' : 'status'}>{status}</p>}
    <section aria-labelledby="cc-action"><h2 id="cc-action">اقدام امروز <small>خروج، کاهش ریسک، سپس ورود</small></h2>
      {usable && (source.actionable.length ? <div className="cc-cards">{source.actionable.map(row => <DecisionCard key={row.symbol} row={row} />)}</div> : <p className="cc-empty">اقدامی در تصمیم‌های فعلی ثبت نشده است.</p>)}</section>
    <section aria-labelledby="cc-ready"><h2 id="cc-ready">زیر نظر امروز <small>آماده / زیر نظر</small></h2>
      {usable && (source.ready.length ? <div className="cc-cards">{source.ready.map(row => <DecisionCard key={row.symbol} row={row} />)}</div> : <p className="cc-empty">نمادی در وضعیت آماده وجود ندارد.</p>)}</section>
    <section aria-labelledby="cc-scanner"><h2 id="cc-scanner">فرصت‌های جدید بازار <small>۱۰ نماد برتر خارج از تاریخچه ثبت‌شده</small></h2>
      <p className="cc-disclaimer">کاندید بررسی — سیگنال خرید نیست</p>
      <p className="cc-meta">نقدشوندگی بر اساس فعالیت نسبی معاملات امروز ارزیابی شده است.</p>
      <p className="cc-meta">بنیادی: داده موجود نیست</p>
      {scanner && <p className="cc-meta">داده اسکنر: {time(scanner.captured_at)} · {scannerStale ? 'داده قدیمی؛ نتایج مربوط به آخرین تصویر ذخیره‌شده بازار است' : 'داده بازار تازه است'}</p>}
      {usable && (liquidityTop10.length
        ? <div className="cc-table-wrap" tabIndex="0" role="region" aria-label="فرصت‌های جدید بازار"><table><thead><tr>
            {['نماد', 'امتیاز اسکنر', 'نقدشوندگی', 'صدک فعالیت', 'ارزش معاملات · میلیارد تومان', 'پول حقیقی · میلیون تومان', 'قدرت خریدار'].map(label => <th key={label} scope="col">{label}</th>)}
          </tr></thead><tbody>{liquidityTop10.map(row => (
            <tr key={row.symbol} data-liquidity={row.liquidity.tier}>
              <th scope="row">{row.symbol}</th>
              <td>{number(typeof row.shortlist_score === 'number' ? row.shortlist_score : (Number(row.discovery_score) || 0) * 20)}</td>
              <td>{LIQUIDITY_LABEL_FA[row.liquidity.tier]}</td>
              <td>{number(row.activity_percentile)}</td>
              <td>{number(row.transaction_value)}</td>
              <td>{number(row.real_money_flow)}</td>
              <td>{number(row.buyer_power)}</td>
            </tr>
          ))}</tbody></table></div>
        : <p className="cc-empty">کاندید جدیدی در فهرست موجود نیست.</p>)}
      <p className="cc-meta">برای بررسی کامل، تاریخچه معتبر نماد موردنظر لازم است.</p>
    </section>
  </main>;
}

export default function CommandCenter() {
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [failed, setFailed] = useState(false), [now, setNow] = useState(Date.now());
  const active = useRef(false), pending = useRef(false);
  const load = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    setData(null); setLoading(true); setFailed(false);
    try {
      // Increased limit so the frontend has the full NEW STRONG_CANDIDATE
      // pool to apply liquidity-aware ordering; does not change Scanner
      // behavior, ranking, or evidence A–E.
      const [decisions, scanner] = await Promise.all([api.get('/api/command-center'), api.get('/api/market/scanner?new_only=true&limit=200')]);
      if (active.current) setData({ decisions, scanner, receivedAt: new Date().toISOString() });
    } catch (error) {
      if (active.current) { setData(null); setFailed(true); }
    } finally {
      pending.current = false;
      if (active.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    active.current = true; load();
    const poll = setInterval(load, 60000), clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { active.current = false; clearInterval(poll); clearInterval(clock); };
  }, [load]);
  return <CommandCenterView data={data} loading={loading} failed={failed} now={now} onRefresh={load} />;
}
